import type {
  ResolvedShippingMethod,
  ShippingAmountRange,
  ShippingMethod,
  ShippingProviderConfig,
  ShippingRate,
  ShippingRuleConditions,
  ShippingRuleContext,
  ShippingVatBasis
} from 'ecosystem'
import { isNullable } from './general'
import { normalizeCountryCode } from './payment'

/** Ids of the two methods synthesised for stores that configure none. */
export const LEGACY_DELIVERY_METHOD_ID = 'delivery'
export const LEGACY_PICKUP_METHOD_ID = 'pickup'

const hasEntries = <T>(list?: T[] | null): list is T[] => Boolean(list?.length)

/** A rate states its own basis, else it inherits the method's, else GROSS. */
export const resolveVatBasis = (
  rateBasis?: ShippingVatBasis | null,
  methodBasis?: ShippingVatBasis | null
): ShippingVatBasis => rateBasis ?? methodBasis ?? 'GROSS'

/**
 * The cart total to compare a threshold against, on the basis that threshold is
 * written on. A `NET` rule in a context that never supplied a net total falls
 * back to the gross one: pricing it as GROSS is wrong in a bounded, visible way,
 * whereas treating the total as 0 would hand out free delivery.
 */
const amountForBasis = (ctx: ShippingRuleContext, basis: ShippingVatBasis): number =>
  basis === 'NET' ? (ctx.netOrderAmount ?? ctx.orderAmount) : ctx.orderAmount

/** `from` inclusive, `to` exclusive — bands tile without gaps. */
const matchesAmount = (range: ShippingAmountRange, amount: number): boolean => {
  if (!isNullable(range.from) && amount < range.from) {
    return false
  }
  return isNullable(range.to) || amount < range.to
}

const matchesCountry = (countries: string[], countryCode?: string | null): boolean => {
  if (isNullable(countryCode) || !countryCode) {
    return false
  }
  const normalized = normalizeCountryCode(countryCode)
  return countries.some((country) => normalizeCountryCode(country) === normalized)
}

/**
 * A single condition holds when every populated field holds (AND). An omitted
 * field means "any"; a populated field whose input is unknown in this context
 * never holds, so evaluation falls through to the next rule instead of
 * guessing (see `ShippingRuleConditions`).
 */
const matchesCondition = (
  condition: ShippingRuleConditions,
  ctx: ShippingRuleContext,
  basis: ShippingVatBasis
): boolean => {
  if (hasEntries(condition.countries) && !matchesCountry(condition.countries, ctx.countryCode)) {
    return false
  }

  if (
    !isNullable(condition.orderAmount) &&
    !matchesAmount(condition.orderAmount, amountForBasis(ctx, basis))
  ) {
    return false
  }

  if (hasEntries(condition.customerType)) {
    if (isNullable(ctx.customerType) || !condition.customerType.includes(ctx.customerType)) {
      return false
    }
  }

  if (hasEntries(condition.vatTreatment)) {
    if (isNullable(ctx.vatTreatment) || !condition.vatTreatment.includes(ctx.vatTreatment)) {
      return false
    }
  }

  return true
}

/**
 * OR across the array, AND within one entry. An empty or omitted list means
 * "no restriction" — the same reading `generatePostnordDeliveryOptions` uses.
 */
export const matchesShippingConditions = (
  conditions: ShippingRuleConditions[] | null | undefined,
  ctx: ShippingRuleContext,
  basis: ShippingVatBasis = 'GROSS'
): boolean => {
  if (!hasEntries(conditions)) {
    return true
  }
  return conditions.some((condition) => matchesCondition(condition, ctx, basis))
}

/**
 * Ordered first-match. Unlike PostNord's cost evaluator there is no implicit
 * "unconditional entries are tried last" reshuffle — array order *is* the
 * priority, which is what the admin UI lets the merchant drag around.
 */
export const resolveShippingRate = (
  rates: ShippingRate[] | null | undefined,
  ctx: ShippingRuleContext,
  methodBasis?: ShippingVatBasis | null
): ShippingRate | null =>
  (rates ?? []).find((rate) =>
    // Each rate is matched on its OWN basis, so one method can price business
    // bands ex-VAT and a consumer band inc-VAT without splitting in two.
    matchesShippingConditions(rate.conditions, ctx, resolveVatBasis(rate.vatBasis, methodBasis))
  ) ?? null

/**
 * Price a matched rate. Returns major units on `basis` — GROSS means
 * VAT-inclusive at the store's rate, as it always has; NET means the merchant
 * typed an ex-VAT figure and the caller must convert before it reaches a
 * payment row.
 */
const priceRate = (
  rate: ShippingRate | null,
  ctx: ShippingRuleContext,
  basis: ShippingVatBasis
): { price: number; isFree: boolean } => {
  if (!rate) {
    return { price: 0, isFree: true }
  }

  if (rate.free) {
    return { price: 0, isFree: true }
  }

  const fee = rate.deliveryFee ?? 0
  const threshold = rate.minimumOrderAmountForFreeDelivery

  // Negative or omitted threshold means "never free" — the convention the
  // PostNord cost config already uses (`-1`).
  if (isNullable(threshold) || threshold < 0) {
    return { price: fee, isFree: fee === 0 }
  }

  // The free-over threshold is written on the same basis as the rest of the
  // rate, so it is compared against the matching cart total.
  return amountForBasis(ctx, basis) >= threshold
    ? { price: 0, isFree: true }
    : { price: fee, isFree: fee === 0 }
}

/**
 * True when any visibility or pricing condition depends on the recipient's
 * address — i.e. on a fact the shopper only supplies *after* a payment session
 * exists. Drives the checkout-topology decision; see `resolveCheckoutTopology`.
 */
export const hasAddressDependentConditions = (
  methods: ShippingMethod[] | null | undefined
): boolean => {
  const dependsOnAddress = (conditions?: ShippingRuleConditions[] | null): boolean =>
    (conditions ?? []).some((condition) => hasEntries(condition.countries))

  return (methods ?? []).some(
    (method) =>
      dependsOnAddress(method.conditions) ||
      (method.rates ?? []).some((rate) => dependsOnAddress(rate.conditions))
  )
}

export interface ResolveShippingMethodsOptions {
  /** Applied to a DELIVERY method whose own rates all miss — normally the
   *  store-level `simpleShipping`, mirroring PostNord's fallback chain. */
  fallbackRate?: ShippingRate | null
}

/**
 * Filter methods by their visibility conditions, price each survivor, sort,
 * and mark one preselected.
 *
 * Returns `[]` when nothing is visible — callers must treat that as a
 * configuration error and fall back, never as "no shipping available".
 * `resolveDefaultShippingMethods` does exactly that.
 */
export const resolveShippingMethods = (
  methods: ShippingMethod[] | null | undefined,
  ctx: ShippingRuleContext,
  { fallbackRate }: ResolveShippingMethodsOptions = {}
): ResolvedShippingMethod[] => {
  const resolved = (methods ?? [])
    .map((method, index) => ({ method, index }))
    .filter(({ method }) =>
      matchesShippingConditions(method.conditions, ctx, resolveVatBasis(null, method.vatBasis))
    )
    .sort((a, b) => (a.method.sortOrder ?? a.index) - (b.method.sortOrder ?? b.index))
    .map(({ method }) => {
      const kind = method.kind ?? 'DELIVERY'
      // Pickup is free unless the merchant priced it; delivery falls back to
      // the store's flat rate.
      const rate =
        resolveShippingRate(method.rates, ctx, method.vatBasis) ??
        (kind === 'PICKUP' ? null : (fallbackRate ?? null))
      // The basis belongs to whichever rate actually won — a NET method that
      // falls through to the store's flat rate must price that fallback as the
      // GROSS figure it is, not as the method's NET.
      const vatBasis = resolveVatBasis(rate?.vatBasis, method.vatBasis)
      const { price, isFree } = priceRate(rate, ctx, vatBasis)

      const entry: ResolvedShippingMethod = {
        id: method.id,
        kind,
        label: method.label,
        price,
        isFree,
        rate,
        preselected: false,
        vatBasis
      }
      if (!isNullable(method.description)) {
        entry.description = method.description
      }
      if (!isNullable(method.pickupIds)) {
        entry.pickupIds = method.pickupIds
      }
      return { entry, isDefault: Boolean(method.preselected) }
    })

  const preselected = resolved.find(({ isDefault }) => isDefault) ?? resolved[0]
  if (preselected) {
    preselected.entry.preselected = true
  }

  return resolved.map(({ entry }) => entry)
}

/** The store-level flat rate expressed as a `ShippingRate`. */
export const simpleShippingToRate = (
  simpleShipping?: ShippingProviderConfig['simpleShipping']
): ShippingRate => ({
  // `simpleShipping` predates the basis and has always been VAT-inclusive at
  // the store's rate. Pinning it explicitly stops it inheriting a NET method's
  // basis when it is used as that method's fallback.
  vatBasis: 'GROSS',
  ...(isNullable(simpleShipping?.deliveryFee) ? {} : { deliveryFee: simpleShipping.deliveryFee }),
  ...(isNullable(simpleShipping?.minimumOrderAmountForFreeDelivery)
    ? {}
    : { minimumOrderAmountForFreeDelivery: simpleShipping.minimumOrderAmountForFreeDelivery }),
  ...(isNullable(simpleShipping?.labels) ? {} : { labels: simpleShipping.labels })
})

export interface ResolveDefaultShippingMethodsOptions extends SynthesizeLegacyMethodsOptions {
  /** Overrides the rate derived from `simpleShipping`. `paymentShippingFactory`
   *  passes its own constant-backed defaults here so an unconfigured store
   *  keeps charging exactly what it charges today. */
  fallbackRate?: ShippingRate | null
}

export interface SynthesizeLegacyMethodsOptions {
  pickupAvailable?: boolean
  deliveryLabel?: string
  pickupLabel?: string
}

/**
 * The two methods every DEFAULT store has implicitly had: one flat delivery
 * option, plus a free pickup option when the store has pickup locations.
 * Configuring nothing must keep behaving exactly like `paymentShippingFactory`
 * + the `DELIVERY | PICKUP` toggle do today.
 *
 * The delivery method deliberately carries no rates of its own — it is priced
 * entirely by the caller's `fallbackRate`, so the flat-fee defaults
 * `paymentShippingFactory` applies to a partially-configured store survive
 * intact.
 */
export const synthesizeLegacyMethods = ({
  pickupAvailable = false,
  deliveryLabel = 'Delivery',
  pickupLabel = 'Pickup'
}: SynthesizeLegacyMethodsOptions = {}): ShippingMethod[] => [
  {
    id: LEGACY_DELIVERY_METHOD_ID,
    kind: 'DELIVERY',
    label: deliveryLabel,
    preselected: true
  },
  ...(pickupAvailable
    ? [
        {
          id: LEGACY_PICKUP_METHOD_ID,
          kind: 'PICKUP' as const,
          label: pickupLabel,
          rates: [{ free: true }]
        }
      ]
    : [])
]

/**
 * The entry point for the DEFAULT shipping branch: configured methods when the
 * store has them, the legacy pair otherwise — and the legacy pair again if a
 * configuration leaves the shopper with nothing to pick, so checkout can never
 * dead-end on a bad rule.
 */
export const resolveDefaultShippingMethods = (
  shippingConfig: ShippingProviderConfig | null | undefined,
  ctx: ShippingRuleContext,
  options: ResolveDefaultShippingMethodsOptions = {}
): ResolvedShippingMethod[] => {
  const fallbackRate = options.fallbackRate ?? simpleShippingToRate(shippingConfig?.simpleShipping)
  const configured = shippingConfig?.defaultOptions?.methods

  const legacy = () =>
    resolveShippingMethods(synthesizeLegacyMethods(options), ctx, { fallbackRate })

  if (!hasEntries(configured)) {
    return legacy()
  }

  const resolved = resolveShippingMethods(configured, ctx, { fallbackRate })
  return resolved.length ? resolved : legacy()
}
